import LogisticsCompany from "@/enum/LogisticsCompany";
import { PagedResult } from "./common.types";
import { get, post } from "@/services/http/platformServer";
import httpClient from "@/services/http/mainServerClient";
import OrderStatus from "@/enum/OrderStatus";
import { WaybillNoType } from "./moreFeatures/shippingCostStat/enum/WaybillNoType";
import EwaybillLogisticsInfoDTO from "@/dto/order/EwaybillLogisticsInfoDTO";
import { retry } from "@/utils/retry";

export enum RefundSyncProgressStatus {
    CANCELED = "CANCELED",
    COMPLETED = "COMPLETED",
    RUNNING = "RUNNING",
}

export enum RefundType {
    EXCHANGE = "EXCHANGE",
    OTHER = "OTHER",
    PRICE_PROTECTION = "PRICE_PROTECTION",
    REFUND_ONLY = "REFUND_ONLY",
    REPAIR = "REPAIR",
    RESEND = "RESEND",
    RETURN_REFUND = "RETURN_REFUND",
}

export enum RefundStatus {
    WAIT_SELLER_AGREE = "WAIT_SELLER_AGREE",
    WAIT_BUYER_RETURN_GOODS = "WAIT_BUYER_RETURN_GOODS",
    WAIT_SELLER_CONFIRM_GOODS = "WAIT_SELLER_CONFIRM_GOODS",
    SELLER_REFUSE_BUYER = "SELLER_REFUSE_BUYER",
    CLOSED = "CLOSED",
    SUCCESS = "SUCCESS",
    WAIT_BUYER_PROCESS = "WAIT_BUYER_PROCESS",
    WAIT_SELLER_SHIP = "WAIT_SELLER_SHIP",
    WAIT_BUYER_CONFIRM_RECEIPT = "WAIT_BUYER_CONFIRM_RECEIPT",
    PROCESSING = "PROCESSING",
}

export enum RefundListTimeField {
    createdTime = "createdTime",
    modifiedTime = "modifiedTime",
    successTime = "successTime",
}

/** 售后列表快捷选单。当前只有有/无售后备注，查询固定走全部满足。 */
export enum RefundQuickCriterion {
    /** 有售后备注 */
    QUICK_HAS_REFUND_REMARK = "QUICK_HAS_REFUND_REMARK",
    /** 无售后备注 */
    QUICK_HAS_NO_REFUND_REMARK = "QUICK_HAS_NO_REFUND_REMARK",
    /** 有订单发货单号 */
    QUICK_HAS_ORDER_WAYBILL_NO = "QUICK_HAS_ORDER_WAYBILL_NO",
}

export interface RefundOrderItemInfoDTO {
    id?: number;
    originalId?: string;
    orderItemId?: number;
    quantity?: number;
    paymentAmount?: number;
    refundAmount?: number;
    price?: number;
    title?: string;
    picture?: string;
    productOriginalId?: string;
    productExternalId?: string;
    skuOriginalId?: string;
    skuExternalId?: string;
    skuProperties?: string;
    isGift?: boolean;
}

/**
 * 关联订单的运单号取号状态
 */
export enum OrderWaybillAcquireStatus {
    ACQUIRED = "ACQUIRED",
    MANUAL = "MANUAL",
    NOT_ACQUIRED = "NOT_ACQUIRED",
    PRINTED = "PRINTED",
    CANCELLED = "CANCELLED",
}

export interface RefundDTO {
    /**
     * 售后类型
     */
    billType?: RefundType;
    /**
     * 平台原始售后类型名称
     */
    billTypeName?: string;
    /**
     * 平台申请时间（买家发起售后的时间）
     */
    createdTime?: string;
    /**
     * 退款单ID
     */
    id?: number;
    /**
     * 退货快递公司
     */
    logisticsCompany?: LogisticsCompany;
    /**
     * 平台最后修改时间
     */
    modifiedTime?: string;

    /**
     * 最后时限
     */
    processingDeadline?: string;

    /**
     * 操作同意退款/拒绝退款的用户ID（卖家）
     * 如果为0(SYSTEM_USER_ID)表示是系统自动退款
     */
    operatingUserId?: number;
    /**
     * 订单ID
     */
    orderId?: number;
    /**
     * 商品项信息列表
     */
    orderItemInfos?: RefundOrderItemInfoDTO[];
    /**
     * 订单编号
     */
    orderOriginalId?: string;
    /**
     * 关联订单的卖家备注（localContent 优先，其次 content）
     */
    orderSellerRemarks?: string;
    /**
     * 退款时的订单状态
     */
    orderStatus?: OrderStatus;
    /**
     * 关联订单的运单号取号状态
     */
    orderWaybillAcquireStatus?: OrderWaybillAcquireStatus;
    /**
     * 关联订单的运单号列表
     */
    orderWaybillNos?: OrderRelatedWaybillNoDTO[];
    /**
     * 平台退款单号
     */
    originalId?: string;
    /**
     * 退款原因
     */
    reason?: string;
    /**
     * 卖家维护的售后备注
     */
    refundRemark?: string;
    /**
     * 运费金额(运费申请退款金额)
     */
    shippingFee?: number;
    /**
     * 实际退款运费金额
     */
    shippingFeeRefundAmount?: number;
    /**
     * 店铺ID
     */
    shopId?: number;
    /**
     * 退款状态
     */
    status: RefundStatus;
    /**
     * 退款完成时间
     */
    successTime?: string;
    /**
     * 实付金额（申请退款的总金额，总金额 = 退款运费 + 退款商品金额 + 退款赠品金额）
     */
    totalPaymentAmount?: number;
    /**
     * 实际退款总金额
     */
    totalRefundAmount?: number;
    /**
     * 退货运单号
     */
    waybillNo?: string;
}

export enum LogisticsStatus {
    Abnormal = "ABNORMAL",
    Delivered = "DELIVERED",
    Delivering = "DELIVERING",
    OnTheWay = "ON_THE_WAY",
    PickedUp = "PICKED_UP",
    UnpickedUp = "UNPICKED_UP",
}

interface OrderRelatedWaybillNoDTO {
    /**
     * 电子面单打印时间，非电子面单为 null。
     */
    datePrinted?: string;
    /**
     * 物流公司
     */
    logisticsCompany?: LogisticsCompany;
    /**
     * 电子面单的物流信息，保存最后一条物流轨迹。
     */
    logisticsInfo?: EwaybillLogisticsInfoDTO;
    /**
     * 电子面单包含的商品信息原始串（Ewaybill.orderItemsInfo），非电子面单为 null。
     */
    orderItemsInfo?: string;
    /**
     * 子母单模式的母单号，非子母单为 null。
     */
    parentWaybillNo?: string;
    /**
     * 运单号
     */
    waybillNo?: string;
    /**
     * 运单号来源类型
     */
    waybillNoType?: WaybillNoType;
}

export interface RefundListParams {
    shopId: number;
    pageNo?: number;
    pageSize?: number;
    offset?: number;
    limit?: number;
    originalIds?: string[];
    orderOriginalIds?: string[];
    waybillNos?: string[];
    timeField?: RefundListTimeField;
    startTime?: string;
    endTime?: string;
    type?: RefundType;
    statuses?: RefundStatus[];
    /** 快捷选单条件全部满足 */
    allMatchQuickCriterionList?: RefundQuickCriterion[];
    /** 售后备注模糊查询 */
    refundRemark?: string;
}

export interface RefundSyncProgress {
    shopId: number;
    taskStartTime: number;
    lastSyncRefundTime?: string;
    completedTime?: string;
    error?: string;
    status: RefundSyncProgressStatus;
}

export interface SyncRefundManuallyParams {
    shopId: number;
    from: string;
    to: string;
}

export interface SyncRefundPair {
    orderOriginalId: string;
    refundOriginalId?: string;
}

export type RefundReturnAddressId = string | number;

export interface RefundReturnAddressDTO {
    returnAddressId: RefundReturnAddressId;
    name?: string;
    mobile?: string;
    telephone?: string;
    province?: string;
    city?: string;
    district?: string;
    street?: string;
    town?: string;
    detailAddress?: string;
    zipCode?: string;
    defaultAddress?: boolean;
}

export interface AgreeRefundParams {
    shopId: number;
    refundId: number;
    username?: string;
    operateDesc?: string;
}

export interface AgreeReturnGoodsParams extends AgreeRefundParams {
    returnAddressId: RefundReturnAddressId;
}

export interface RefundSetting {
    shopId: number;
    refundApiTokenUsername?: string;
}

export function getRefundList(params: RefundListParams, config?): Promise<PagedResult<RefundDTO>> {
    return post<PagedResult<RefundDTO>>(params.shopId, "/refund/list", params, config);
}

export type MultiShopRefundCountParams = Omit<
    RefundListParams,
    "shopId" | "pageNo" | "pageSize" | "offset" | "limit"
> & {
    shopIds: number[];
};

export function getMultiShopRefundCount(params: MultiShopRefundCountParams) {
    return post<{ shopId: number; totalRefunds: number }[]>(params.shopIds[0], "/refund/multi_shop_count", params, {
        hideModal: true,
    });
}

export function getTaobaoRefundApiTokenUsernames(shopId: number): Promise<string[]> {
    return get<string[]>(shopId, "/refund/api_token_usernames", { shopId });
}

export async function getRefundSetting(shopId: number): Promise<RefundSetting> {
    return get<RefundSetting>(shopId, "/refund/setting/get", { shopId });
}

export function saveRefundSetting(setting: RefundSetting): Promise<RefundSetting> {
    return post<RefundSetting>(setting.shopId, "/refund/setting/save", setting);
}

export function getRefundReturnAddresses(shopId: number): Promise<RefundReturnAddressDTO[]> {
    return get<RefundReturnAddressDTO[]>(shopId, "/refund/return_address/list", { shopId });
}

export function agreePlatformRefund(params: AgreeRefundParams, config?) {
    return post(params.shopId, "/refund/agree", params, config);
}

export function agreeReturnGoods(params: AgreeReturnGoodsParams, config?) {
    return post(params.shopId, "/refund/return_goods/agree", params, config);
}

export function syncRefundIncrementally(shopId: number, config?) {
    return post<RefundSyncProgress>(shopId, "/refund/sync/incrementally", { shopId }, config);
}

export function stopSyncRefundIncrementally(shopId: number, config?) {
    return post<{ successful: boolean }>(shopId, "/refund/sync/incrementally/stop", { shopId }, config);
}

export function syncRefundManually(params: SyncRefundManuallyParams) {
    return post<RefundSyncProgress>(params.shopId, "/refund/sync/manually", params);
}

export function stopSyncRefundManually(shopId: number) {
    return post<{ successful: boolean }>(shopId, "/refund/sync/manually/stop", { shopId });
}

export function syncRefundByOriginalIds(shopId: number, refundPairs: SyncRefundPair[]) {
    return post<{ successful: boolean }>(shopId, "/refund/sync/by_original_ids", { shopId, refundPairs });
}

export function resyncRefund(shopId: number, orderOriginalId: string, refundOriginalId: string) {
    return post<{ successful: boolean }>(shopId, "/refund/sync/resync", {
        shopId,
        orderOriginalId,
        refundOriginalId,
    });
}

interface UpdateRefundResult {
    refundId: number;
    success: boolean;
    errorMessage: string;
}

export function batchUpdateRefundMark(shopId: number, refundIds: number[], refundRemark: string) {
    return post<{ shopId: number; results: UpdateRefundResult[] }>(shopId, "/refund/batch_update_refund_remark", {
        shopId,
        refundIds,
        refundRemark,
    });
}

enum RefundLogType {
    AgreeRefund = "AGREE_REFUND",
    AgreeReturnGoods = "AGREE_RETURN_GOODS",
    UpdateRemark = "UPDATE_REMARK",
}

export interface RefundLogDTO {
    /**
     * 操作描述
     */
    content?: string;
    /**
     * 本地 ID
     */
    id?: number;
    /**
     * 客户端 IP
     */
    ip?: string;
    /**
     * 售后单 ID
     */
    refundId?: number;
    /**
     * 操作时间
     */
    time?: string;
    /**
     * 操作类型
     */
    type?: RefundLogType;
    /**
     * 操作类型描述
     */
    typeDescription?: string;
    /**
     * 操作人用户名
     */
    username?: string;
}

export function getRefundLog(shopId: number, refundId: number) {
    return post<RefundLogDTO[]>(shopId, "/refund/log/list_by_refund_id", {
        shopId,
        refundId,
    });
}

export interface ShopRefunds {
    shopId: number;
    refunds: RefundDTO[];
    scanTime: number;
}

/** 运单号只保留英文字母、数字和 -，其余字符都当作分隔符，再拼成英文逗号分隔参数。 */
const refundWaybillNoSeparatorRegExp = /[^A-Za-z0-9-]+/g;

export function formatRefundWaybillNos(waybillNos: string) {
    return waybillNos
        .split(refundWaybillNoSeparatorRegExp)
        .filter((it) => it)
        .join(",");
}

export interface GetRefundByWaybillNoParams {
    shopIds: number[];
    // 批量扫描场景使用:多个运单号使用英文逗号隔开
    waybillNos?: string;
    // 单个扫描场景使用;
    waybillNo?: string;
}

/**
 * 根据退货运单号获取退款单信息。
 * 单个扫描传 waybillNo，批量扫描传 waybillNos。
 */
export function getRefundByWaybillNo(params: GetRefundByWaybillNoParams) {
    const { shopIds, waybillNo, waybillNos } = params;

    return get<ShopRefunds[]>(shopIds[0], "/refund/scan/get_by_waybill_no", {
        shopIds: shopIds.join(),
        waybillNo,
        waybillNos,
    });
}

interface ScanRecord {
    waybillNo: string;
    refunds: RefundDTO[];
}

export interface CreateRefundScanRecordParams {
    scanBatchNo: string;
    scanTime: number;
    scanRecords: ScanRecord[];
}

export interface RefundScanRecordDTO {
    scanBatchNo: string;
    scanTime: number;
    waybillNo: string;
    refunds: RefundDTO[];
    shopId: number;
    operateUsername: string;
}

export interface RefundScanRecordQueryParams {
    scanStartTime?: string;
    scanEndTime?: string;
    shopIds: number[];
    scanBatchNo?: string;
    waybillNo?: string;
    pageNo: number;
    pageSize: number;
}

export interface RefundScanRecordExportParams extends RefundScanRecordQueryParams {
    deduplicateByWaybillNo: boolean;
}

export interface RefundScanRecordExportTask {
    taskId: string;
    status: string;
    downloadUrl?: string;
}

function formatRefundScanRecordQueryParams(params: RefundScanRecordQueryParams) {
    const { shopIds, ...otherParams } = params;

    return {
        ...otherParams,
        shopIds: shopIds?.join(),
    };
}

/** 保存售后扫描记录。 */
export function createRefundScanRecord(params: CreateRefundScanRecordParams) {
    return retry(async () => {
        await httpClient.post("/refund_scan_record/create", params, { hideModal: true });
    }, 5);
}

/** 获取售后扫描记录列表。 */
export function getRefundScanRecordList(params: RefundScanRecordQueryParams) {
    return httpClient.get<PagedResult<RefundScanRecordDTO>>(
        "/refund_scan_record/list",
        formatRefundScanRecordQueryParams(params),
    );
}

/** 提交售后扫描记录导出任务。 */
export function submitRefundScanRecordExportTask(params: RefundScanRecordExportParams) {
    return httpClient.post<RefundScanRecordExportTask>(
        "/refund_scan_record/export",
        formatRefundScanRecordQueryParams(params),
    );
}

/** 查询售后扫描记录导出任务结果。 */
export function getRefundScanRecordExportResult(taskId: string) {
    return httpClient.get<RefundScanRecordExportTask>("/refund_scan_record/export/result", { taskId });
}

export function generateRefundBatchNo() {
    return httpClient.post<{ scanBatchNo: string }>("/refund_scan_record/generate_scan_batch_no");
}
